import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useScribeIdentity } from "../hooks/useScribeIdentity";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { Logo } from "brand";
import {
  useActiveOrg,
  GateHeader,
  FullScreenLoader,
  isShowInActiveWindow,
} from "common-fe";
import { useTranslation } from "i18n-fe";
import { useShows } from "../hooks/useShows";
import { useScribeAssignments, soleShow } from "../hooks/useScribeAssignments";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useRidesForClass } from "../hooks/useRidesForClass";
import { useRidesByClassId } from "../hooks/useRidesByClassId";
import {
  classNumberLabel,
  formatVenueTime,
  nextAssignedClass,
  nextUpcomingRide,
} from "../util/classDisplay";
import { rideCnoNumber } from "../util/rideDisplay";
import { scribePositionsFor } from "../util/scribeAssignment";
import { useScoreContext } from "../context/ScoreContext";
import { RESUME_ROUTE_KEY } from "../hooks/useInactivityRedirect";
import { fullSignOut } from "../auth/signOut";
import { getScribePin } from "../util/scribePin";
import AdminOptions from "./AdminOptions";
import ScribeLockPin from "./ScribeLockPin";
import SplitClassChip from "./SplitClassChip";
import VenueClock from "./VenueClock";

// #2078 — GateHeader's `brandLogo` slot is empty by default (a gate is a
// dead end, not a place to navigate from — see GateHeader.jsx's own doc
// comment), so this lock screen never passed one, which also collapsed the
// rightContent account block to the left edge (AppBar.jsx lays the two out
// with `justify-content: space-between`; with only one child present it
// sits at flex-start instead of flex-end). Deliberately NOT TopBar's
// `BrandWordmark` (that one navigates to /classes on click) — this is a
// static mark, matching the "dead end" doctrine the gate already documents.
function GateBrandMark() {
  const { t } = useTranslation("common");
  return (
    <Box data-testid="gate-brand-logo" sx={{ display: "flex", alignItems: "center" }}>
      <Logo
        style={{
          paddingLeft: 8,
          height: 40,
          width: "auto",
          marginRight: 16,
          flexShrink: 0,
        }}
      />
      <Typography variant="h2" sx={{ fontSize: "1.1rem" }}>
        {t("app.title")}
      </Typography>
    </Box>
  );
}

// #3568 -- the clock and every scheduled time on this screen are ALWAYS 12-hour.
// The #710 12/24 control is gone (so is store/timeFormat.js): a tablet at the ring
// has one format, never a per-tablet setting a scribe can leave in the wrong state.
//
// #1889 (decision 2026-08-29) — this clock is VENUE time, rendered by the
// same shared VenueClock component the TopBar carries on every other scribe
// view. This DELIBERATELY REVERSES #1558, which made the standby clock
// browser-local on the reasoning that a scribe checking this screen from
// off-site should see their own wall clock: the standing rule settled the
// other way — every time we display is venue time, because venue time is
// always meaningful (especially when you are NOT physically at the venue),
// while a device clock on a scribe's laptop with a wrong timezone is exactly
// the failure the venue-zone convention exists to prevent. The top bar
// clock and this one are both 12-hour, so /setup and the top bar can never
// disagree, and a show with no
// venueTimezone renders VenueClock's loud unconfigured state (AC6) rather
// than a plausible Pacific default — including on the first-run picker,
// where no show is selected yet and there is no venue to be on.

// #1558 — the next-class/next-ride block just below the clock formats
// `nextClass.scheduledTime` / `nextRide.scheduledTime`, the SAME
// FVD-reconciled genuine-UTC field ClassList/RideList/SessionHeader render
// (#805) — a real show/ride time, not the live `new Date()` instant the
// clock above ticks on. So this one goes through the FVD-stamp-zone
// formatVenueTime (util/classDisplay.js, #1811 — NOT the show's real
// venueTimezone), with `hour12` wired to this screen's stored 12h/24h
// 12-hour (#3568). Callers fall back to
// the literal em dash "—" themselves — formatVenueTime returns "" for a
// missing/invalid iso, this screen's own placeholder for "unknown".
export default function InitialSetup() {
  const navigate = useNavigate();
  const { t } = useTranslation("score");
  const {
    selectedShow,
    startSession,
    isSessionReady,
    pickerUnlocked,
    lockPicker,
    online,
  } = useScoreContext();
  // Active org drives which org's shows score-api returns (X-Org-Id header).
  // Single-org scribes are auto-selected by ActiveOrgProvider; super/multi-org
  // users pick it here before the show (card #479).
  const { orgs, activeOrgId, setActiveOrgId } = useActiveOrg();
  const { data: shows, loading, error: showsError } = useShows();
  const { removeUser } = useAuth();
  // #3256: falls back to the last known scribe when offline with no session.
  const { sub } = useScribeIdentity();

  // #524/#627 — a scribe assigned within exactly one show skips the picker
  // entirely: the session is just that show. Only relevant on a cold start (no
  // persisted session); once a show is set, this screen isn't reachable except
  // through the lock — and an admin who just unlocked it must not be auto-skipped
  // straight back out (#628).
  const autoSkipEligible = !isSessionReady && !pickerUnlocked;
  const { assignments, loading: loadingAssignments } =
    useScribeAssignments(autoSkipEligible ? shows : null);
  const sole = autoSkipEligible ? soleShow(assignments) : null;
  // Keep the picker hidden while we're still deciding, or when we're about to
  // auto-skip — so the picker never flashes before the redirect.
  //
  // #3632: this is for the FIRST resolve only. Once the picker has been on screen,
  // an org switch re-runs the assignments fetch, and replacing the picker with a
  // loader then throws away the org the scribe just picked. After the first
  // paint the picker stays up (a sole show still auto-skips, via the effect below).
  const pickerShownRef = useRef(false);
  const resolvingAutoSkip =
    autoSkipEligible &&
    !pickerShownRef.current &&
    (loadingAssignments || assignments === null || Boolean(sole));

  useEffect(() => {
    if (!sole) return;
    startSession(sole);
    navigate("/classes", { replace: true });
  }, [sole, startSession, navigate]);

  const [showId, setShowId] = useState("");

  // #2222 — scribe's own picker (a react-select fed by useShows(), NOT
  // ActiveShowContext/ShowSwitcher — this screen is scribe's real show
  // picker, see the file-level note above the component) lists every show
  // the org has ever ingested. Filter to the active date window
  // (isShowInActiveWindow, shared with common-fe's ShowSwitcher), with the
  // same "Show all past shows" escape hatch so an old show stays reachable,
  // never gone. Purely client-side over the already-fetched/cached `shows`
  // list — no extra fetch, no change to useShows()/score-api.
  const [showAllShows, setShowAllShows] = useState(false);
  const visibleShows = useMemo(() => {
    const all = shows ?? [];
    if (showAllShows) return all;
    return all.filter((s) => isShowInActiveWindow(s));
  }, [shows, showAllShows]);

  const handleOrgChange = (e) => {
    setActiveOrgId(e.target.value);
    // Shows are org-scoped — a stale selection from the previous org is invalid.
    setShowId("");
  };
  // R1-UI-003 / #628 — the org + show pickers are behind the PIN lock. A
  // first-time visitor (no session yet) has nothing to protect, so they're open;
  // after that, only a PIN unlock reopens them.
  const selectorsUnlocked = !isSessionReady || pickerUnlocked;

  // #2122 — CARD APPROACH (read before touching this chain again). The card
  // named five hook calls as a serial chain: useShows ->
  // useScribeAssignments(shows) -> useClassesForShow(selectedShow.id) ->
  // useRidesByClassId(assignedClassIds) -> useRidesForClass(nextClass.id),
  // each apparently waiting on the prior's resolved data. Reading each hook
  // (not measuring) split that into what's a GENUINE data dependency and
  // what was an artificial hop this component's own wiring introduced. Two
  // of the claims below were checked by actually running the Cypress specs
  // in cypress/e2e/setup-next-class-prefetch.cy.js and mutating the wiring
  // each one exercises, not just by reading — the GENUINE and TESTED
  // CONCURRENT sections have a surviving spec behind them. The other two
  // ATTEMPTED-AND-REVERTED notes below don't: they record two different
  // production changes this card tried, tested as far as a Cypress network
  // assertion could, and then backed out once that testing turned out
  // unable to establish what it needed to.
  //
  //   GENUINE (kept sequential, can't be removed without an API change):
  //     shows -> assignments: useScribeAssignments calls score-api's
  //     POST /class/batch with the show ids (apps/score-api/src/handlers/
  //     class.js listClassesForShows) — classes are only queryable by id or
  //     by the classesByShow GSI (showId), there is no "mine, org-wide"
  //     read path, so the show ids must exist first. Adding one is a
  //     score-api change, out of scope here (apps/scribe-fe only, and
  //     score-api is a sibling card's active file, #1978).
  //     assignments -> classes: needs a concrete showId.
  //     classes -> ridesByClassId: needs concrete classIds.
  //
  //   ATTEMPTED-AND-REVERTED #1, ridesByClassId -> nextClass ->
  //   useRidesForClass(nextClass.id): an earlier version of this comment
  //   claimed this pair was already-concurrent on the strength of #856's
  //   "no entry yet == not complete" default, and shipped a redundant
  //   "prefetch" hook call to force it. Testing that claim directly (delay
  //   one assigned class's rides heavily, assert the other's ride request
  //   still lands fast) never went red no matter how that pair was wired,
  //   including with the prefetch hook removed entirely — which led to the
  //   discovery in FOUND BUT NOT FIXED HERE below instead of a real proof,
  //   so that claim and the prefetch hook were both dropped.
  //
  //   ATTEMPTED-AND-REVERTED #2, assignments/sole -> classes: this card
  //   tried keying useClassesForShow off `sole?.id ?? selectedShow?.id`
  //   instead of `selectedShow?.id` alone, on the theory that on the
  //   auto-skip path `selectedShow` only updates a render AFTER `sole`
  //   resolves (it flows through startSession -> ScoreContext), and since
  //   that same effect calls navigate() in the same tick, the classes
  //   fetch could lose the race entirely. Measured: reverting to
  //   `selectedShow?.id` alone and re-running the auto-skip spec, the
  //   classes-for-show request still fired. NOT established: why. This
  //   component isn't the only caller of useClassesForShow(selectedShow.id)
  //   — ClassList.jsx:56 calls it too, and ClassList is exactly where the
  //   auto-skip navigate() lands; useCachedResource
  //   (packages/common-fe/src/utils/useCachedResource.js) has no in-flight
  //   dedupe or shared cache-key registry, so a Cypress network assertion
  //   can't attribute an observed request to InitialSetup rather than
  //   ClassList. The `sole?.id` change was therefore reverted: an
  //   unjustified line in this file is worse than no line, and the
  //   measured behavior (a classes-for-show request lands either way) is
  //   equally consistent with "ClassList's own fetch was doing the work
  //   all along." Kept as history here rather than silently dropped, since
  //   the same underlying gap (useCachedResource's lack of dedupe) is one
  //   root cause behind #2126.
  //
  //   TESTED CONCURRENT, not actually a chained hop: useShows is declared
  //   FIRST and reads as a prerequisite for everything below it, but for a
  //   scribe who already has a session, useClassesForShow only needs
  //   `selectedShow.id` — already available from the persisted session on
  //   the very first render (ScoreContext.jsx's useState initializer reads
  //   localStorage synchronously) — and never waits on the shows list at
  //   all. Proven (and mutation-tested: gating the classes call on `shows`
  //   being loaded turns it red) in
  //   cypress/e2e/setup-next-class-prefetch.cy.js's first spec.
  //
  //   FOUND BUT NOT FIXED HERE: while building the (later-abandoned) proof
  //   above for the ridesByClassId/nextClass pair, EVERY cache-first hook in
  //   this app was observed firing its network fetch TWICE on a cold mount,
  //   independent of any of this card's changes. Root cause traced to
  //   useApiConfig (packages/common-fe/src/utils/useApiConfig.js): its
  //   useCallback depends on `activeOrgId`, which resolves asynchronously
  //   after the org-list call — so useScoreApi's `api` object gets a new
  //   identity once org resolves, and since `api` sits in every
  //   useCachedResource hook's dependency array, that single identity change
  //   re-fires every hook's fetch a second time. That doubles first-load
  //   network traffic on every screen, not just this one, which is squarely
  //   the kind of thing #979 (the slow-first-load card this one was split
  //   from) is about — but it lives in packages/common-fe, this card's
  //   sibling #2120's territory (BLOCKED awaiting a decision from Jaybill),
  //   so it's out of scope to fix here. Carded separately as #2126 rather
  //   than left only in this comment.
  //
  //   REJECTED:
  //     - Prefetching classes for the scribe's TENTATIVE (not yet confirmed
  //       by clicking "Start Scoring") dropdown pick on the manual picker.
  //       Rejected: the same assignedClasses/nextClass state also drives the
  //       always-visible "Next assigned class" preview above the picker, so
  //       warming it from an unconfirmed pick could flash a different show's
  //       next class while the scribe is still choosing — an ambiguous
  //       state, not a faster one.
  //     - An org-wide "my classes" score-api endpoint that would remove the
  //       shows -> assignments hop outright. Real option, but it's a
  //       score-api change and this card's scope is apps/scribe-fe only.
  //
  // #647 — the standby screen's "next assigned class": useClassesForShow
  // already filters to this scribe's assignments and sorts by scheduledTime
  // (#627/#632), so the first one that isn't already done IS the next class.
  // showId is only real once a session exists; useClassesForShow(null) is a
  // no-op (no request), so this is safe before a show is picked. Keyed on
  // `selectedShow?.id` (context), NOT the locally-known `sole` — see
  // ATTEMPTED-AND-REVERTED #2 above for why routing `sole` in here directly
  // was tried and backed out.
  const { data: assignedClasses } = useClassesForShow(selectedShow?.id ?? null);
  // #856 — "done" is judged by isClassComplete on the class's OWN rides
  // (Class.status is dead, never written), so nextAssignedClass needs every
  // assigned class's rides to be SURE, but treats a class it hasn't heard
  // back about yet as "not complete" rather than blocking
  // (isClassComplete(undefined) === false). That means nextClass below
  // resolves off assignedClasses ALONE, without needing ridesByClassId to
  // have answered anything yet, so useRidesForClass(nextClass?.id) further
  // down fires in the same render as useRidesByClassId rather than after
  // it — not a sixth serial hop despite reading as one top to bottom.
  const assignedClassIds = useMemo(
    () => (assignedClasses ?? []).map((c) => c.id),
    [assignedClasses],
  );
  const { data: ridesByClassId } = useRidesByClassId(assignedClassIds);
  const nextClass = useMemo(
    () => nextAssignedClass(assignedClasses ?? [], ridesByClassId ?? {}),
    [assignedClasses, ridesByClassId],
  );
  // #647 — same seat-resolution rule as ClassList (scribeAssignments), not a
  // second one. Normally a single position; joined for the rare multi-seat case.
  const nextClassPosition = useMemo(
    () => scribePositionsFor(nextClass, sub).join(" / "),
    [nextClass, sub],
  );
  // #647 — the next ride within that class, off the same source of truth
  // #636 established (useRidesForClass + classDisplay's nextUpcomingRide).
  const { data: nextClassRides } = useRidesForClass(nextClass?.id ?? null);
  const nextRide = useMemo(
    () => nextUpcomingRide(nextClassRides?.rides ?? []),
    [nextClassRides],
  );

  // R1-#144 — resume destination: the route the scribe was on when
  // inactivity kicked in, falling back to their assigned class.
  const resumeDest = useMemo(() => {
    try {
      const stashed = sessionStorage.getItem(RESUME_ROUTE_KEY);
      if (stashed) return stashed;
    } catch {
      // sessionStorage unavailable — fall through to assigned-class fallback.
    }
    if (nextClass) return `/classes/${nextClass.id}`;
    return "/classes";
  }, [nextClass]);

  // #3568/#3635 -- leaving the lock screen (the right PIN resumes scoring; there
  // is no Cancel) or Admin Options (Back) both land the scribe back on scoring. Re-lock first so the next padlock tap asks for the
  // PIN again, then go to where they were (or their next assigned class).
  const goBackToScoring = () => {
    try {
      sessionStorage.removeItem(RESUME_ROUTE_KEY);
    } catch {
      // non-fatal
    }
    lockPicker();
    navigate(resumeDest, { replace: true });
  };

  const canStart = Boolean(showId);

  const handleStart = () => {
    const show = (shows ?? []).find((s) => s.id === showId);
    if (!show) return;
    startSession(show);
    navigate("/classes", { replace: true });
  };

  // #524 — while resolving assignments (or about to auto-skip a single-
  // assignment scribe), show a loader rather than flashing the picker.
  // #515 — this is the one true full-viewport route-level bootstrap loader
  // in scribe-fe (no AppShell chrome exists yet); reuse the shared
  // common-fe FullScreenLoader (#514) instead of a hand-rolled Box, same
  // dark-mode-safe color it already carries.
  if (resolvingAutoSkip) {
    return <FullScreenLoader />;
  }

  // #881 — /setup is the one screen a scribe reaches on a stale/wrong active
  // org (kiosk lockdown, #734, otherwise has no escape hatches at all) before
  // a scoring session exists, so it's the closest thing this app has to
  // scoreadmin/announcer's entitlement/role gate. The GateHeader gives it a
  // visible sign-out plus the active org name.
  const gateHeader = (
    <GateHeader
      onLogout={() => fullSignOut(removeUser)}
      brandLogo={<GateBrandMark />}
      accountOrgsUrl={`${import.meta.env.VITE_ACCOUNT_FE_BASE_URL}/orgs`}
    />
  );

  // #3568 -- ONE locked screen and ONE unlocked screen. Locked (a session exists
  // and the PIN has not been entered): the clock, the next assigned class and a PIN
  // input. Unlocked (the right PIN, or a first run with no session yet): Admin
  // Options, with Select Show in it and a single Back button.
  if (selectorsUnlocked) {
    const hasPick = Boolean(showId);
    // Back applies a show picked here, otherwise just returns to scoring. With no
    // session yet there is nowhere to go back to, so the same button starts scoring.
    const handleBack = () => (hasPick ? handleStart() : goBackToScoring());
    pickerShownRef.current = true;
    return (
      <>
        {gateHeader}
        <AdminOptions
          online={online}
          onBack={handleBack}
          backLabel={isSessionReady ? t("setup.back") : t("setup.start")}
          backDisabled={!isSessionReady && !canStart}
        >
          <Stack spacing={3}>
            <Typography variant="h6" component="h2" fontWeight={500}>
              {t("setup.title")}
            </Typography>
            {orgs.length > 1 && (
              <FormControl fullWidth>
                <InputLabel>{t("setup.orgLabel")}</InputLabel>
                <Select
                  value={activeOrgId ?? ""}
                  label={t("setup.orgLabel")}
                  onChange={handleOrgChange}
                  disabled={loading}
                >
                  {orgs.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>{t("setup.showLabel")}</InputLabel>
              <Select
                value={showId}
                label={t("setup.showLabel")}
                onChange={(e) => setShowId(e.target.value)}
                disabled={loading}
              >
                {visibleShows.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                    {s.dateRange ? ` — ${s.dateRange}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* #2222 — reachability escape hatch: the show FormControl above only
                lists shows in the active date window, so this stays visible off the
                RAW `shows` list (never `visibleShows`) whenever the org has any
                shows at all — otherwise there would be no way to reach the toggle
                that reveals an out-of-window one. */}
            {(shows ?? []).length > 0 && (
              <FormControlLabel
                data-testid="setup-show-all-toggle"
                control={
                  <Switch
                    size="small"
                    checked={showAllShows}
                    onChange={(e) => setShowAllShows(e.target.checked)}
                    inputProps={{ "aria-label": t("setup.showAllToggle") }}
                  />
                }
                label={t("setup.showAllToggle")}
                sx={{ mt: -1, "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
              />
            )}

            {/* #1737 — an org-scoped local cache miss (a device that hasn't
                refreshed since #1736 shipped, or a genuinely show-less org) plus
                either forced-offline or a failed fetchRemote (a real no-signal
                venue) used to render this as a silent empty dropdown with no
                explanation and no recovery hint. Treat "couldn't reach the
                network" and "did reach it but the call failed" the same way: both
                mean the empty list isn't necessarily the truth. */}
            {!loading && (shows ?? []).length === 0 && (
              <Typography color="text.secondary" data-testid="setup-no-shows">
                {online && !showsError ? t("setup.noShows") : t("setup.noShowsOffline")}
              </Typography>
            )}

            {/* #734 — kiosk lockdown: Account settings sits behind the PIN lock,
                only reachable here, never from a free-standing menu. */}
            <Button
              variant="text"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => {
                window.location.href =
                  import.meta.env.VITE_ACCOUNT_FE_BASE_URL + "/settings";
              }}
              sx={{ alignSelf: "flex-start" }}
            >
              Account settings
            </Button>
          </Stack>
        </AdminOptions>
      </>
    );
  }

  return (
    <>
      {gateHeader}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          pt: "64px",
          bgcolor: "background.default",
        }}
      >
        <Card elevation={4} sx={{ width: "100%", maxWidth: 560, borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Logo style={{ width: 160, height: "auto" }} />
            </Box>

            {/* R1-UI-003 — informational standby block. */}
            <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
              {/* #1889 — same shared VenueClock as the TopBar's, rendered big for
                  the standby card. Keeps the historical "wall-clock" testid. */}
              <VenueClock
                venueTimezone={selectedShow?.venueTimezone}
                hasShow={Boolean(selectedShow)}
                testId="wall-clock"
                variant="h3"
                sx={{ fontFamily: "monospace", letterSpacing: 3, fontWeight: 700 }}
              />
              <Divider flexItem />
              <Stack data-testid="next-class-block" alignItems="center" spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t("lock.nextClass")}
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {nextClass ? classNumberLabel(nextClass) : t("lock.noClass")}
                </Typography>
                {/* #1761 — a split section gets the same Split/RO chip as the day
                    sheet instead of an identical-looking label. */}
                <SplitClassChip klass={nextClass} testId="setup-next-class-split-chip" />
                {nextClass && (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ color: "text.secondary", fontSize: 14 }}
                  >
                    <span>
                      {formatVenueTime(nextClass.scheduledTime, {
                        hour12: true,
                        zone: nextClass.scheduledTimeZone,
                      }) || "—"}
                    </span>
                    <span>·</span>
                    <span>{nextClass.ring ?? "Ring —"}</span>
                    {nextClassPosition && (
                      <>
                        <span>·</span>
                        <span>Position {nextClassPosition}</span>
                      </>
                    )}
                  </Stack>
                )}
                {/* #647 — the next RIDE within that class (time + CNO + rider). */}
                {nextClass && nextRide && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {`${
                      formatVenueTime(nextRide.scheduledTime, {
                        hour12: true,
                        zone: nextRide.scheduledTimeZone,
                      }) || "—"
                    } · ${t("rides.ridePrefix", { number: rideCnoNumber(nextRide) })}${
                      nextRide.riderName && nextRide.riderName !== "—"
                        ? ` · ${nextRide.riderName}`
                        : ""
                    }`}
                  </Typography>
                )}
              </Stack>
            </Stack>

            <ScribeLockPin
              pin={getScribePin(selectedShow)}
              label={t("lock.resumePinLabel")}
              onUnlock={goBackToScoring}
            />
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
